import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from "react";

import { Link, useNavigate } from "react-router-dom";
import { Tab } from "@headlessui/react";
import { AuthContext } from "../../contexts/AuthContext";
import hasUserRole from "../../utils/UserUtil";
import {
  ChevronLeft,
  Calendar,
  Download,
  Search,
  ChevronDown,
  Users,
  CheckCircle,
  ClipboardList,
  BarChart2,
  TrendingUp,
  Target,
  ArrowLeft,
  ArrowRight,
  Building2,
  AlertTriangle,
  XCircle,
  CalendarIcon,
  Bell,
  MessageSquare,
  Loader,
  ChevronUp,
  Filter,
  SlackIcon,
  ExternalLink,
  X,
  MapPin,
  Clock,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../../services/api";
import completedSurveyService from "../../services/completedSurveyService";
import {
  calculateComplianceRate,
  calculateComplianceSummary,
  filterFormComplianceRows,
  formatComplianceRate,
  isComplianceApplicable,
  normalizeFormComplianceRows,
  sortFormComplianceRows,
} from "../../utils/formCompliance";
import toast from 'react-hot-toast';

// Utility function for conditional class names
const classNames = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

const CompliancePage = () => {
  const { user } = useContext(AuthContext);
  const [participantData, setParticipantData] = useState([]);
  const [siteComplianceData, setSiteComplianceData] = useState([]);
  const [formComplianceData, setFormComplianceData] = useState([]);
  const [complianceStats, setComplianceStats] = useState({
    totalParticipants: 0,
    applicableParticipants: 0,
    compliantParticipants: 0,
    complianceRate: null,
    averageCompliance: null,
    totalCompletedSurveys: 0,
    totalSurveys: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSite, setSelectedSite] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  const [studyId, setStudyId] = useState(null);

  const [siteSearchTerm, setSiteSearchTerm] = useState("");
  const [siteSortConfig, setSiteSortConfig] = useState({ key: 'complianceRate', direction: 'descending' });
  const [siteCurrentPage, setSiteCurrentPage] = useState(1);
  const [siteItemsPerPage] = useState(10);
  const [formSearchTerm, setFormSearchTerm] = useState("");
  const [formSortConfig, setFormSortConfig] = useState({ key: 'complianceRate', direction: 'ascending' });
  const [formCurrentPage, setFormCurrentPage] = useState(1);
  const [formItemsPerPage] = useState(10);
  // FECOA-1192: per-form drill-down modal (participant status for a form)
  const EMPTY_FORM_COUNTS = { completed: 0, in_progress: 0, pending: 0, missed: 0, total: 0 };
  const [selectedForm, setSelectedForm] = useState(null);
  const [formParticipants, setFormParticipants] = useState([]);
  const [formParticipantCounts, setFormParticipantCounts] = useState(EMPTY_FORM_COUNTS);
  const [formTotalParticipants, setFormTotalParticipants] = useState(0);
  const [formParticipantsLoading, setFormParticipantsLoading] = useState(false);
  const [formParticipantsError, setFormParticipantsError] = useState(null);
  const [formStatusFilter, setFormStatusFilter] = useState('all'); // all | completed | in_progress | pending | missed
  const [formParticipantSearch, setFormParticipantSearch] = useState('');
  const [scoringAvailable, setScoringAvailable] = useState(false);
  const [exportingScores, setExportingScores] = useState(false);
  const navigate = useNavigate();
  const loadedRef = useRef(false); // dev-only guard to prevent StrictMode double-run

  useEffect(() => {
    // Dev guard: prevent double-fetch in React 18 StrictMode
    if (import.meta?.env?.DEV && loadedRef.current) {
      return;
    }
    loadedRef.current = true;

    const loadData = async () => {
      try {
        setLoading(true);
        const currentStudyId = window.location.pathname.split('/')[2];
        setStudyId(currentStudyId);

        console.log('Loading compliance data using aggregated summary endpoint for study:', currentStudyId);

        // Check if scoring rules exist for any assessments in this study
        try {
          const scoringCheck = await api.get(`/api/assessment-builder/studies/${currentStudyId}/has-scoring/`);
          setScoringAvailable(scoringCheck.data?.has_scoring === true);
        } catch (scoringErr) {
          console.log('Scoring check not available:', scoringErr);
          setScoringAvailable(false);
        }

        // Check for missed surveys across all participants in the study (non-blocking)
        try {
          const missedSurveysResult = await completedSurveyService.checkMissedSurveysForStudy(currentStudyId);
          if (missedSurveysResult?.missed_surveys_created > 0) {
            console.log(`Created ${missedSurveysResult.missed_surveys_created} missed survey records across ${missedSurveysResult.total_participants} participants`);
            toast.success(`Created ${missedSurveysResult.missed_surveys_created} missed survey records`);
          }
        } catch (missedError) {
          console.error("Error checking for missed surveys:", missedError);
        }

        // Single aggregated request
        const summaryResp = await api.get(`/api/studies/${currentStudyId}/assessments/summary`);
        const data = summaryResp.data || {};

        // Participants
        const participants = data.participants || [];

        // Sites summary may come as data.sites or data.site_summary
        const siteSummary = data.sites || data.site_summary || [];
        const formComplianceList = normalizeFormComplianceRows(data.forms || data.form_summary || []);

        // Map participants to the shape expected by UI (gracefully handle missing fields)
        const participantComplianceData = participants.map(p => {
          const completedSurveys = Number(p.completedSurveys ?? p.completed ?? p.completed_count ?? p.completed_surveys ?? 0);
          const missedSurveys = Number(p.missedSurveys ?? p.missed ?? p.missed_count ?? p.missed_surveys ?? 0);
          const totalSurveys = Number(p.totalSurveys ?? p.total ?? p.total_surveys ?? (completedSurveys + missedSurveys));
          const isApplicable = totalSurveys > 0;

          return {
            participantId: p.participantId || p.participant_id || p.id || null,
            participantUUID: p.participantUUID || p.participant_uuid || p.uuid || null,
            participantEmail: p.participantEmail || p.participant_email || p.email || null,
            site: p.site || p.site_name || p.siteName || null,
            site_id: p.site_id || p.siteId || null,
            completedSurveys,
            missedSurveys,
            totalSurveys,
            complianceRate: isApplicable
              ? Number(p.complianceRate ?? p.compliance_rate ?? calculateComplianceRate(completedSurveys, totalSurveys))
              : null,
            isComplianceApplicable: isApplicable,
            // Map survey arrays from API response with fallbacks
            dailySurveys: p.dailySurveys || p.daily_surveys || [],
            scheduledSurveys: p.scheduledSurveys || p.scheduled_surveys || []
          };
        });

        const participantSiteStats = new Map();
        participantComplianceData.forEach(participant => {
          if (!participant.site_id) return;
          const siteStats = participantSiteStats.get(participant.site_id) || {
            totalParticipants: 0,
            applicableParticipants: 0,
            compliantParticipants: 0,
          };
          siteStats.totalParticipants += 1;
          if (participant.isComplianceApplicable) {
            siteStats.applicableParticipants += 1;
            if (participant.complianceRate >= 80) {
              siteStats.compliantParticipants += 1;
            }
          }
          participantSiteStats.set(participant.site_id, siteStats);
        });

        // Build site compliance data if not provided
        let siteComplianceList;
        if (siteSummary.length > 0 && (siteSummary[0]?.siteId || siteSummary[0]?.site_id || siteSummary[0]?.siteName)) {
          siteComplianceList = siteSummary.map(s => {
            const siteId = s.siteId || s.site_id || s.id;
            const totalCompletedSurveys = Number(s.totalCompletedSurveys ?? s.total_completed_surveys ?? s.completed ?? s.completed_count ?? 0);
            const totalMissedSurveys = Number(s.totalMissedSurveys ?? s.total_missed_surveys ?? s.missed ?? s.missed_count ?? 0);
            const totalSurveys = Number(s.totalSurveys ?? s.total_surveys ?? s.total ?? totalCompletedSurveys + totalMissedSurveys);
            const derivedStats = participantSiteStats.get(siteId) || {};
            const applicableParticipants = derivedStats.applicableParticipants ?? 0;
            const compliantParticipants = derivedStats.compliantParticipants ?? 0;

            return {
              siteName: s.siteName || s.site_name || s.name || 'Site',
              siteId,
              totalParticipants: derivedStats.totalParticipants ?? Number(s.totalParticipants ?? s.total_participants ?? 0),
              applicableParticipants,
              compliantParticipants,
              complianceRate: calculateComplianceRate(totalCompletedSurveys, totalSurveys),
              totalCompletedSurveys,
              totalMissedSurveys,
              totalSurveys,
              isComplianceApplicable: totalSurveys > 0,
              compliantParticipantsRate: applicableParticipants > 0
                ? (compliantParticipants / applicableParticipants) * 100
                : null,
            };
          });
        } else {
          // Derive site aggregates from participants
          const siteComplianceMap = new Map();
          participantComplianceData.forEach(pd => {
            if (!pd.site_id) return;
            if (!siteComplianceMap.has(pd.site_id)) {
              siteComplianceMap.set(pd.site_id, {
                siteName: pd.site || 'Site',
                siteId: pd.site_id,
                totalParticipants: 0,
                applicableParticipants: 0,
                compliantParticipants: 0,
                complianceRate: null,
                totalCompletedSurveys: 0,
                totalMissedSurveys: 0,
                totalSurveys: 0,
                compliantParticipantsRate: null
              });
            }
            const s = siteComplianceMap.get(pd.site_id);
            s.totalParticipants += 1;
            if (!pd.isComplianceApplicable) return;

            s.applicableParticipants += 1;
            s.totalCompletedSurveys += (pd.completedSurveys || 0);
            s.totalMissedSurveys += (pd.missedSurveys || 0);
            s.totalSurveys += (pd.totalSurveys || 0);
            if (pd.complianceRate >= 80) {
              s.compliantParticipants += 1;
            }
          });
          siteComplianceList = Array.from(siteComplianceMap.values()).map(s => {
            s.complianceRate = calculateComplianceRate(s.totalCompletedSurveys, s.totalSurveys);
            s.isComplianceApplicable = s.totalSurveys > 0;
            s.compliantParticipantsRate = s.applicableParticipants > 0
              ? (s.compliantParticipants / s.applicableParticipants) * 100
              : null;
            return s;
          }).sort((a, b) => (b.complianceRate ?? -1) - (a.complianceRate ?? -1));
        }

        // Apply site filtering based on user permissions
        let filteredParticipantData = participantComplianceData;
        let filteredSiteData = siteComplianceList;
        let filteredFormData = formComplianceList;
        
        // If user is not admin and has site restrictions, filter data
        if (user && !hasUserRole(user, ['Program Admin', 'Project Admin']) && !user?.is_superuser) {
          if (user.assigned_sites && user.assigned_sites.length > 0) {
            const userSiteIds = user.assigned_sites.map(site => site.id || site.site_id);
            const userSiteNames = user.assigned_sites.map(site => site.name || site.site_name);
            
            // Filter participants to only show those from user's assigned sites
            filteredParticipantData = participantComplianceData.filter(participant => 
              userSiteIds.includes(participant.site_id) || userSiteNames.includes(participant.site)
            );
            
            // Filter site compliance data to only show user's assigned sites
            filteredSiteData = siteComplianceList.filter(site => 
              userSiteIds.includes(site.siteId) || userSiteNames.includes(site.siteName)
            );

            filteredFormData = formComplianceList
              .map(form => {
                const filteredSites = (form.siteBreakdown || []).filter(site =>
                  userSiteIds.includes(site.siteId) || userSiteNames.includes(site.siteName)
                );

                if (filteredSites.length === 0) return null;

                const completedSurveys = filteredSites.reduce((acc, site) => acc + (site.completedSurveys || 0), 0);
                const totalSurveys = filteredSites.reduce((acc, site) => acc + (site.totalSurveys || 0), 0);
                const missedSurveys = filteredSites.reduce((acc, site) => acc + (site.missedSurveys || 0), 0);
                const complianceRate = calculateComplianceRate(completedSurveys, totalSurveys);

                return {
                  ...form,
                  siteBreakdown: filteredSites,
                  completedSurveys,
                  totalSurveys,
                  missedSurveys,
                  assignedParticipants: filteredSites.reduce((acc, site) => acc + (site.assignedParticipants || 0), 0),
                  participantIds: [...new Set(filteredSites.flatMap(site => site.participantIds || []))].sort(),
                  complianceRate,
                  missedRate: calculateComplianceRate(missedSurveys, totalSurveys),
                  isComplianceApplicable: totalSurveys > 0,
                };
              })
              .filter(Boolean);
          }
        }

        const summaryStats = calculateComplianceSummary(filteredParticipantData);

        // Set state with filtered data
        setParticipantData(filteredParticipantData);
        setSiteComplianceData(filteredSiteData);
        setFormComplianceData(filteredFormData);
        setComplianceStats({
          totalParticipants: summaryStats.totalParticipants,
          applicableParticipants: summaryStats.applicableParticipants,
          compliantParticipants: summaryStats.compliantParticipants,
          complianceRate: summaryStats.complianceRate,
          averageCompliance: summaryStats.averageCompliance,
          totalCompletedSurveys: summaryStats.totalCompletedSurveys,
          totalSurveys: summaryStats.totalSurveys
        });
      } catch (err) {
        console.error('Error fetching compliance data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);



  // Format a date string to a human-readable format
  const formatDateString = (dateStr) => {
    if (!dateStr) return "N/A";
    
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateStr;
    }
  };



  // Organize surveys by type (daily vs visit)
  const organizeParticipantSurveys = (participant) => {
    if (!participant || !participant.dailySurveys || !participant.scheduledSurveys) {
      return { dailySurveys: [], scheduledSurveys: [] };
    }

    return {
      dailySurveys: participant.dailySurveys || [],
      scheduledSurveys: participant.scheduledSurveys || []
    };
  };

  const getDailySurveyCount = (dailySurveys) => (
    typeof dailySurveys === 'number' ? dailySurveys : (dailySurveys ? dailySurveys.length : 0)
  );

  const getScheduledSurveyCount = (scheduledSurveys) => {
    if (typeof scheduledSurveys === 'number') return scheduledSurveys;
    if (!Array.isArray(scheduledSurveys)) return 0;

    return scheduledSurveys.reduce(
      (total, visit) => total + (Array.isArray(visit?.surveys) ? visit.surveys.length : 1),
      0
    );
  };

  const getComplianceBarClass = (value) => {
    if (!Number.isFinite(value)) return "bg-gray-300 dark:bg-gray-600";
    if (value >= 80) return "bg-primary-500";
    if (value >= 60) return "bg-emerald-500";
    if (value >= 40) return "bg-yellow-500";
    if (value >= 20) return "bg-orange-500";
    return "bg-red-500";
  };

  const getComplianceTextClass = (value) => {
    if (!Number.isFinite(value)) return "text-gray-500 dark:text-gray-400 font-medium";
    if (value >= 80) return "text-primary-600 font-medium";
    if (value >= 60) return "text-emerald-600 font-medium";
    if (value >= 40) return "text-yellow-600 font-medium";
    if (value >= 20) return "text-orange-600 font-medium";
    return "text-red-600 font-medium";
  };

  const getComplianceFill = (value) => {
    if (!Number.isFinite(value)) return "#D1D5DB";
    if (value >= 80) return "#0D9488";
    if (value >= 60) return "#34D399";
    if (value >= 40) return "#FBBF24";
    if (value >= 20) return "#F97316";
    return "#EF4444";
  };

  const ComplianceRateDisplay = ({ value, widthUnit = "%" }) => (
    <div className="flex items-center">
      <div className="h-2 w-20 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${getComplianceBarClass(value)}`}
          style={{ width: Number.isFinite(value) ? `${Math.min(100, value)}${widthUnit}` : "0%" }}
        ></div>
      </div>
      <span className={`ml-3 ${getComplianceTextClass(value)}`}>
        {formatComplianceRate(value)}
      </span>
    </div>
  );

  // Generate data for the compliance distribution chart
  const getComplianceDistribution = () => {
    const distributionData = [
      { range: "0-20%", count: 0, color: "#EF4444" },
      { range: "21-40%", count: 0, color: "#F97316" },
      { range: "41-60%", count: 0, color: "#FBBF24" },
      { range: "61-80%", count: 0, color: "#34D399" },
      { range: "81-100%", count: 0, color: "#0D9488" },
    ];

    participantData.filter(isComplianceApplicable).forEach((participant) => {
      const complianceRate = participant.complianceRate;
      if (complianceRate <= 20) {
        distributionData[0].count += 1;
      } else if (complianceRate <= 40) {
        distributionData[1].count += 1;
      } else if (complianceRate <= 60) {
        distributionData[2].count += 1;
      } else if (complianceRate <= 80) {
        distributionData[3].count += 1;
      } else {
        distributionData[4].count += 1;
      }
    });

    return distributionData;
  };

  // Get compliance data for pie chart
  const getCompliancePieData = () => {
    return [
      { name: "Compliant", value: complianceStats.compliantParticipants, color: "#0D9488" },
      { name: "Non-Compliant", value: complianceStats.applicableParticipants - complianceStats.compliantParticipants, color: "#EF4444" }
    ];
  };

  // Get participant table columns
  const getParticipantTableColumns = useCallback(() => {
    return [
      {
        Header: "Participant ID",
        accessor: "participantId",
        Cell: ({ row }) => {
          // Validate required fields for the link
          const hasValidData = row.original.site_id && row.original.participantUUID && studyId;
          
          if (hasValidData) {
            return (
              <div>
                <Link
                  to={`/studies/${studyId}/sites/${row.original.site_id}/participant/${row.original.participantUUID}`}
                  className="text-primary-600 hover:underline font-medium"
                >
                  {row.original.participantId}
                </Link>
              </div>
            );
          } else {
            // If we don't have valid data, show as plain text
            return (
              <div>
                <span className="text-gray-900 dark:text-gray-100 font-medium" title="Participant details unavailable">
                  {row.original.participantId}
                </span>
              </div>
            );
          }
        },
      },
      {
        Header: "Compliance Rate",
        accessor: "complianceRate",
        Cell: ({ value }) => (
          <ComplianceRateDisplay value={value} />
        ),
      },
      {
        Header: "Daily Surveys",
        accessor: (row) => {
          return getDailySurveyCount(row.dailySurveys);
        },
      },
      {
        Header: "Visit Surveys",
        accessor: (row) => {
          return getScheduledSurveyCount(row.scheduledSurveys);
        },
      },
      {
        Header: "Completed",
        accessor: "completedSurveys",
      },
      {
        Header: "Missed",
        accessor: "missedSurveys",
      },
    ];
  }, [studyId]);

  // Display survey type icon
  const SurveyTypeIcon = ({ type }) => {
    switch (type) {
      case "Daily":
        return <CalendarIcon className="w-4 h-4 text-blue-500" />;
      case "Patient Prompted":
        return <Bell className="w-4 h-4 text-purple-500" />;
      case "Scheduled":
        return <Calendar className="w-4 h-4 text-primary-500" />;
      default:
        return <ClipboardList className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
    }
  };

  // Display survey status
  const SurveyStatus = ({ status }) => {
    if (status === "completed") {
      return (
        <div className="flex items-center">
          <CheckCircle className="w-4 h-4 text-primary-500 mr-1" />
          <span className="text-sm text-primary-600 font-medium">Completed</span>
        </div>
      );
    } else if (status === "missed") {
      return (
        <div className="flex items-center">
          <XCircle className="w-4 h-4 text-red-500 mr-1" />
          <span className="text-sm text-red-600 font-medium">Missed</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center">
          <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1" />
          <span className="text-sm text-yellow-600 font-medium">{status}</span>
        </div>
      );
    }
  };

  // StatCard component for displaying metrics
  const StatCard = ({ title, value, icon: Icon, color = "primary", subtitle = null }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm dark:shadow-gray-900/20 p-6 border border-gray-100 dark:border-gray-700 hover:shadow-md dark:hover:shadow-gray-900/20 transition-all duration-300">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full bg-${color}-50`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );



  // Handle search for sites
  const handleSiteSearchChange = (e) => {
    setSiteSearchTerm(e.target.value);
    setSiteCurrentPage(1);
  };

  // Handle sorting for sites table
  const requestSiteSort = (key) => {
    let direction = 'ascending';
    if (siteSortConfig.key === key && siteSortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSiteSortConfig({ key, direction });
  };

  // Filter and sort site data
  const filteredSiteData = useMemo(() => {
    // First filter by search term
    const filtered = siteComplianceData.filter(site => 
      site.siteName.toLowerCase().includes(siteSearchTerm.toLowerCase())
    );
    
    // Then sort the filtered data
    if (siteSortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[siteSortConfig.key];
        const bValue = b[siteSortConfig.key];

        if (!Number.isFinite(aValue) && Number.isFinite(bValue)) return 1;
        if (Number.isFinite(aValue) && !Number.isFinite(bValue)) return -1;

        // Handle numeric values like complianceRate
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return siteSortConfig.direction === 'ascending'
            ? aValue - bValue
            : bValue - aValue;
        }
        
        // Handle string values like siteName
        if (aValue < bValue) {
          return siteSortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return siteSortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [siteComplianceData, siteSearchTerm, siteSortConfig]);

  // Paginate site data
  const currentSiteItems = useMemo(() => {
    const indexOfLastItem = siteCurrentPage * siteItemsPerPage;
    const indexOfFirstItem = indexOfLastItem - siteItemsPerPage;
    return filteredSiteData.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredSiteData, siteCurrentPage, siteItemsPerPage]);

  // Handle site pagination
  const paginateSites = (pageNumber) => setSiteCurrentPage(pageNumber);

  const handleFormSearchChange = (e) => {
    setFormSearchTerm(e.target.value);
    setFormCurrentPage(1);
  };

  const requestFormSort = (key) => {
    let direction = 'ascending';
    if (formSortConfig.key === key && formSortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setFormSortConfig({ key, direction });
  };

  const filteredFormData = useMemo(() => (
    sortFormComplianceRows(
      filterFormComplianceRows(formComplianceData, formSearchTerm),
      formSortConfig
    )
  ), [formComplianceData, formSearchTerm, formSortConfig]);

  const currentFormItems = useMemo(() => {
    const indexOfLastItem = formCurrentPage * formItemsPerPage;
    const indexOfFirstItem = indexOfLastItem - formItemsPerPage;
    return filteredFormData.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredFormData, formCurrentPage, formItemsPerPage]);

  const totalFormPages = Math.ceil(filteredFormData.length / formItemsPerPage);

  // FECOA-1192: open the drill-down modal for a clicked form and fetch the
  // per-participant status (completed / in progress / pending / missed) for it.
  const openFormParticipants = useCallback(async (form) => {
    if (!form || form.formId == null || !studyId) return;
    setSelectedForm(form);
    setFormStatusFilter('all');
    setFormParticipantSearch('');
    setFormParticipants([]);
    setFormParticipantCounts(EMPTY_FORM_COUNTS);
    setFormTotalParticipants(0);
    setFormParticipantsError(null);
    setFormParticipantsLoading(true);
    try {
      const resp = await api.get(
        `/api/studies/${studyId}/assessments/forms/${form.formId}/participants/`
      );
      setFormParticipants(resp.data?.participants || []);
      setFormParticipantCounts({ ...EMPTY_FORM_COUNTS, ...(resp.data?.counts || {}) });
      setFormTotalParticipants(resp.data?.totalParticipants || 0);
    } catch (err) {
      console.error('Error loading form participants:', err);
      setFormParticipantsError(
        'Unable to load participants for this form. Please try again.'
      );
    } finally {
      setFormParticipantsLoading(false);
    }
  }, [studyId]);

  const closeFormParticipants = useCallback(() => {
    setSelectedForm(null);
    setFormParticipants([]);
    setFormParticipantCounts(EMPTY_FORM_COUNTS);
    setFormTotalParticipants(0);
    setFormParticipantsError(null);
    setFormParticipantsLoading(false);
    setFormStatusFilter('all');
    setFormParticipantSearch('');
  }, []);

  // Esc-to-close + body scroll lock while the drill-down modal is open.
  useEffect(() => {
    if (!selectedForm) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeFormParticipants();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [selectedForm, closeFormParticipants]);

  const visibleFormParticipants = useMemo(() => {
    const q = formParticipantSearch.trim().toLowerCase();
    return formParticipants.filter((p) => {
      if (formStatusFilter !== 'all' && p.status !== formStatusFilter) return false;
      if (!q) return true;
      return (
        (p.participantId || '').toLowerCase().includes(q) ||
        (p.participantEmail || '').toLowerCase().includes(q) ||
        (p.site || '').toLowerCase().includes(q)
      );
    });
  }, [formParticipants, formStatusFilter, formParticipantSearch]);

  // Status badge shared by the drill-down table (4 compliance states).
  const FORM_STATUS_BADGE = {
    completed: { label: 'Completed', icon: CheckCircle, className: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' },
    in_progress: { label: 'In Progress', icon: Loader, className: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
    pending: { label: 'Pending', icon: Clock, className: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
    missed: { label: 'Missed', icon: XCircle, className: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
  };

  const formStats = useMemo(() => {
    const summary = calculateComplianceSummary(formComplianceData);
    return {
      totalForms: formComplianceData.length,
      applicableForms: summary.applicableRows,
      averageCompliance: summary.averageCompliance,
      lowComplianceForms: formComplianceData.filter(form => (form.totalSurveys || 0) > 0 && (form.complianceRate || 0) < 80).length,
      totalCompleted: summary.totalCompletedSurveys,
      totalSurveys: summary.totalSurveys,
    };
  }, [formComplianceData]);

  const siteStats = useMemo(() => calculateComplianceSummary(siteComplianceData), [siteComplianceData]);
  const siteComplianceChartData = useMemo(
    () => siteComplianceData.filter(isComplianceApplicable),
    [siteComplianceData]
  );

  // Filter and sort participant data
  const filteredParticipants = useMemo(() => {
    const filtered = participantData.filter(participant => {
      // Filter by search term (participant ID or site). Emails are excluded:
      // participant emails are auto-generated UUIDs, so matching them makes
      // short numeric searches hit seemingly unrelated rows.
      const matchesSearch = searchTerm === "" ||
        participant.participantId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        participant.site?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filter by selected site
      const matchesSite = selectedSite === "all" || 
        participant.site_id?.toString() === selectedSite?.toString() ||
        participant.siteId?.toString() === selectedSite?.toString();
      
      return matchesSearch && matchesSite;
    });

    // Sort by site code first, then by participant ID (both ascending)
    filtered.sort((a, b) => {
      // Extract site code and participant ID from participantId (format: "1000-1001")
      const getParticipantParts = (participantId) => {
        if (!participantId || typeof participantId !== 'string') return { siteCode: '', participantNum: '' };
        const parts = participantId.split('-');
        return {
          siteCode: parts[0] || '',
          participantNum: parts[1] || ''
        };
      };

      const aData = getParticipantParts(a.participantId);
      const bData = getParticipantParts(b.participantId);

      // First sort by site code (ascending)
      if (aData.siteCode !== bData.siteCode) {
        return aData.siteCode.localeCompare(bData.siteCode);
      }

      // Then sort by participant number (ascending, numeric comparison)
      const aNum = parseInt(aData.participantNum) || 0;
      const bNum = parseInt(bData.participantNum) || 0;
      return aNum - bNum;
    });

    return filtered;
  }, [participantData, searchTerm, selectedSite]);

  // Paginate participant data
  const currentParticipants = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredParticipants.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredParticipants, currentPage, itemsPerPage]);

  // Calculate total pages for participants
  const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);

  // Export site compliance data to CSV
  const handleSiteExport = () => {
    const headers = [
      'Site Name', 
      'Compliance Rate (%)', 
      'Compliant Participants', 
      'Total Active Participants', 
      'Completed Surveys', 
      'Total Surveys'
    ];
    
    const csvContent = [
      headers.join(','),
      ...filteredSiteData.map(site => 
        [
          site.siteName,
          formatComplianceRate(site.complianceRate),
          site.compliantParticipants,
          site.totalParticipants,
          site.totalCompletedSurveys,
          site.totalSurveys
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `site_compliance_export.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleFormExport = () => {
    const headers = [
      'Form Name',
      'Schedule Type',
      'Compliance Rate (%)',
      'Completed Surveys',
      'In Progress Surveys',
      'Pending Surveys',
      'Missed Surveys',
      'Total Surveys',
      'Assigned Participants'
    ];

    const csvContent = [
      headers.join(','),
      ...filteredFormData.map(form =>
        [
          `"${(form.formName || '').replace(/"/g, '""')}"`,
          form.scheduleType,
          formatComplianceRate(form.complianceRate),
          form.completedSurveys,
          form.inProgressSurveys,
          form.pendingSurveys,
          form.missedSurveys,
          form.totalSurveys,
          form.assignedParticipants
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `form_compliance_export.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Export scores for assessments with scoring rules
  const handleScoreExport = async () => {
    if (!studyId) return;
    
    setExportingScores(true);
    try {
      const response = await api.get(`/api/assessment-builder/studies/${studyId}/scores/export/`);
      const scores = response.data?.scores || [];
      
      if (scores.length === 0) {
        toast.error('No scores available to export');
        return;
      }
      
      // Build CSV from scores
      const headers = [
        'Participant ID',
        'Survey Name',
        'Score Name',
        'Score Code',
        'Scoring Type',
        'Raw Score',
        'Rescaled Score',
        'Items Answered',
        'Items In Score',
        'Interpretation',
        'Calculated At'
      ];
      
      const csvContent = [
        headers.join(','),
        ...scores.map(score => 
          [
            score.participant_id || '',
            `"${(score.survey_name || '').replace(/"/g, '""')}"`,
            `"${(score.score_name || '').replace(/"/g, '""')}"`,
            score.score_code || '',
            score.scoring_type || '',
            score.raw_score ?? '',
            score.rescaled_score ?? '',
            score.items_answered ?? '',
            score.items_in_score ?? '',
            `"${(score.interpretation || '').replace(/"/g, '""')}"`,
            score.calculated_at || ''
          ].join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `study_scores_export.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      toast.success(`Exported ${scores.length} scores`);
    } catch (err) {
      console.error('Error exporting scores:', err);
      toast.error('Failed to export scores');
    } finally {
      setExportingScores(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-6" />
          <p className="text-gray-600 dark:text-gray-300 text-lg font-medium">Loading compliance data...</p>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">Please wait while we gather your information.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md p-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl dark:shadow-gray-900/20">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Something went wrong</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Custom tooltip for compliance charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg dark:shadow-gray-900/20">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">{payload[0].name}</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">{payload[0].value}</span> participants
          </p>
        </div>
      );
    }
    return null;
  };

  // Main dashboard render
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate(`/studies/${studyId}`)}
            className="group flex items-center text-primary-600 hover:text-primary-800 transition-all duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1 group-hover:-translate-x-1 transition-transform duration-200" />
            <span className="font-medium">Back to Study</span>
          </button>
          <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between">
<h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Study Compliance</h1>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md dark:shadow-gray-900/20 overflow-hidden">
          <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
            <Tab.List className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 pt-4">
              <Tab
                className={({ selected }) =>
                  classNames(
                    "px-4 py-3 text-sm font-medium focus:outline-none rounded-t-lg mr-2",
                    "border-b-2 transition-colors duration-200",
                    selected
                      ? "text-primary-700 border-primary-500 bg-white dark:bg-gray-800"
                      : "text-gray-500 dark:text-gray-400 border-transparent hover:text-primary-600 hover:border-primary-300"
                  )
                }
              >
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  <span>Participants</span>
                </div>
              </Tab>
              <Tab
                className={({ selected }) =>
                  classNames(
                    "px-4 py-3 text-sm font-medium focus:outline-none rounded-t-lg mr-2",
                    "border-b-2 transition-colors duration-200",
                    selected
                      ? "text-primary-700 border-primary-500 bg-white dark:bg-gray-800"
                      : "text-gray-500 dark:text-gray-400 border-transparent hover:text-primary-600 hover:border-primary-300"
                  )
                }
              >
                <div className="flex items-center">
                  <Building2 className="w-4 h-4 mr-2" />
                  <span>Sites</span>
                </div>
              </Tab>
              <Tab
                className={({ selected }) =>
                  classNames(
                    "px-4 py-3 text-sm font-medium focus:outline-none rounded-t-lg mr-2",
                    "border-b-2 transition-colors duration-200",
                    selected
                      ? "text-primary-700 border-primary-500 bg-white dark:bg-gray-800"
                      : "text-gray-500 dark:text-gray-400 border-transparent hover:text-primary-600 hover:border-primary-300"
                  )
                }
              >
                <div className="flex items-center">
                  <ClipboardList className="w-4 h-4 mr-2" />
                  <span>Forms</span>
                </div>
              </Tab>
              <Tab
                className={({ selected }) =>
                  classNames(
                    "px-4 py-3 text-sm font-medium focus:outline-none rounded-t-lg",
                    "border-b-2 transition-colors duration-200",
                    selected
                      ? "text-primary-700 border-primary-500 bg-white dark:bg-gray-800"
                      : "text-gray-500 dark:text-gray-400 border-transparent hover:text-primary-600 hover:border-primary-300"
                  )
                }
              >
                <div className="flex items-center">
                  <BarChart2 className="w-4 h-4 mr-2" />
                  <span>Statistics</span>
                </div>
              </Tab>
            </Tab.List>
            <Tab.Panels>
              {/* Participant Tab */}
              <Tab.Panel>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="p-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <StatCard
                      title="Total Active Participants"
                      value={complianceStats.totalParticipants}
                      icon={Users}
                      color="blue"
                    />
                    <StatCard
                      title="Compliance Rate"
                      value={formatComplianceRate(complianceStats.complianceRate)}
                      icon={Target}
                      color={Number.isFinite(complianceStats.complianceRate) && complianceStats.complianceRate >= 80 ? "primary" : "red"}
                      subtitle={`${complianceStats.compliantParticipants} of ${complianceStats.applicableParticipants} applicable participants are compliant`}
                    />
                    <StatCard
                      title="Survey Completion"
                      value={`${complianceStats.totalCompletedSurveys} / ${complianceStats.totalSurveys}`}
                      icon={CheckCircle}
                      color="emerald"
                      subtitle={`${formatComplianceRate(calculateComplianceRate(complianceStats.totalCompletedSurveys, complianceStats.totalSurveys))} of applicable surveys completed`}
                    />
                  </div>
                
                  <div className="mb-8">
                    <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900/20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 mb-8">
                      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Participant Compliance</h2>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Individual participant performance metrics</p>
                        </div>
                        <div className="flex flex-col md:flex-row items-center space-y-3 md:space-y-0 md:space-x-3">
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                            </div>
                            <input
                              type="text"
                              placeholder="Search participants..."
                              className="pl-10 py-2 pr-4 block w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 sm:text-sm"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </div>
                          <div className="flex items-center space-x-2">
                            <label htmlFor="site-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                              Site:
                            </label>
                            <select
                              id="site-filter"
                              className="block pl-3 pr-10 py-2.5 text-base border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 sm:text-sm rounded-lg"
                              value={selectedSite}
                              onChange={(e) => setSelectedSite(e.target.value)}
                            >
                              <option value="all">All Sites</option>
                              {siteComplianceData.map((site) => (
                                <option key={site.siteId} value={site.siteId}>
                                  {site.siteName}
                                </option>
                              ))}
                            </select>
                          </div>
                          <button
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                            onClick={() => {
                              const csvContent = [
                                "Participant ID,Site,Compliance Rate (%),Daily Surveys,Scheduled Surveys,Completed,Missed",
                                ...filteredParticipants.map(participant => 
                                  `${participant.participantId},${participant.site},${formatComplianceRate(participant.complianceRate)},${getDailySurveyCount(participant.dailySurveys)},${getScheduledSurveyCount(participant.scheduledSurveys)},${participant.completedSurveys},${participant.missedSurveys}`
                                )
                              ].join('\n');

                              const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                              const link = document.createElement('a');
                              if (link.download !== undefined) {
                                const url = URL.createObjectURL(blob);
                                link.setAttribute('href', url);
                                link.setAttribute('download', `participant_compliance_export.csv`);
                                link.style.visibility = 'hidden';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            }}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Export
                          </button>
                          {scoringAvailable && (
                            <button
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 disabled:opacity-50"
                              onClick={handleScoreExport}
                              disabled={exportingScores}
                            >
                              {exportingScores ? (
                                <>
                                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <BarChart2 className="h-4 w-4 mr-2" />
                                  Export Scores
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <AnimatePresence>
                      {currentParticipants.length === 0 ? (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          transition={{ duration: 0.3 }}
                          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg py-12 text-center shadow-sm dark:shadow-gray-900/20"
                        >
                          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                            <Search className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No participants found</h3>
                          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                            No participants match your current search criteria. Try adjusting your filters or search terms.
                          </p>
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm dark:shadow-gray-900/20"
                        >
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead>
                              <tr className="bg-gray-50 dark:bg-gray-900">
                                {getParticipantTableColumns().map((column) => (
                                  <th
                                    key={column.Header}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                  >
                                    {column.Header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {currentParticipants.map((participant) => (
                                <tr key={participant.participantUUID} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                                  {getParticipantTableColumns().map((column) => (
                                    <td 
                                      key={column.Header} 
                                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300"
                                    >
                                      {column.Cell ? 
                                        column.Cell({ 
                                          row: { original: participant }, 
                                          value: column.accessor ? 
                                            (typeof column.accessor === 'function' ? 
                                              column.accessor(participant) : 
                                              participant[column.accessor]
                                            ) : null 
                                        }) : 
                                        (typeof column.accessor === 'function' ? 
                                          column.accessor(participant) : 
                                          participant[column.accessor]
                                        )
                                      }
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    
                    {totalPages > 1 && (
                      <div className="flex justify-between items-center mt-5">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className={`flex items-center px-4 py-2 rounded-lg text-sm transition-colors duration-200 ${
                            currentPage === 1
                              ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                              : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                        >
                          <ArrowLeft className="w-4 h-4 mr-1" />
                          Previous
                        </button>
                        
                        <div className="flex items-center space-x-1">
                          {[...Array(totalPages).keys()].map((number) => {
                            const pageNumber = number + 1;
                            const isActive = pageNumber === currentPage;
                            
                            // Display a limited number of page buttons
                            if (
                              pageNumber === 1 ||
                              pageNumber === totalPages ||
                              Math.abs(pageNumber - currentPage) <= 1
                            ) {
                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => setCurrentPage(pageNumber)}
                                  className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors duration-200 ${
                                    isActive
                                      ? "bg-primary-600 text-white"
                                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  }`}
                                >
                                  {pageNumber}
                                </button>
                              );
                            } else if (
                              (pageNumber === 2 && currentPage > 3) ||
                              (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                            ) {
                              return (
                                <span key={pageNumber} className="text-gray-500 dark:text-gray-400 px-1">
                                  ...
                                </span>
                              );
                            }
                            
                            return null;
                          })}
                        </div>
                        
                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className={`flex items-center px-4 py-2 rounded-lg text-sm transition-colors duration-200 ${
                            currentPage === totalPages
                              ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                              : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                        >
                          Next
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              </Tab.Panel>
              
              {/* Site Compliance Tab */}
              <Tab.Panel>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="p-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <StatCard
                      title="Total Sites"
                      value={siteComplianceData.length}
                      icon={Building2}
                      color="primary"
                    />
                    <StatCard
                      title="Avg. Compliance Rate"
                      value={formatComplianceRate(siteStats.averageCompliance)}
                      icon={CheckCircle}
                      color="emerald"
                    />
                    <StatCard
                      title="Compliant Sites"
                      value={`${siteStats.compliantRows} of ${siteStats.applicableRows}`}
                      icon={Target}
                      color="blue"
                    />
                    <StatCard
                      title="Total Active Participants"
                      value={siteComplianceData.reduce((acc, site) => acc + site.totalParticipants, 0)}
                      icon={Users}
                      color="violet"
                    />
                  </div>

                  {/* Site Compliance Table */}
                  <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900/20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 mb-10">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Site Compliance</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Performance metrics across all study sites</p>
                      </div>
                      <div className="flex flex-col md:flex-row items-center space-y-3 md:space-y-0 md:space-x-3">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-gray-400 dark:text-gray-500" />
                          </div>
                          <input
                            type="text"
                            className="pl-10 py-2 pr-4 block w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 sm:text-sm"
                            placeholder="Search sites..."
                            value={siteSearchTerm}
                            onChange={handleSiteSearchChange}
                          />
                        </div>
                        <button
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                          onClick={handleSiteExport}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Export
                        </button>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            {[
                              { key: 'siteName', label: 'Site Name' },
                              { key: 'complianceRate', label: 'Compliance Rate (%)' },
                              { key: 'compliantParticipants', label: 'Compliant Participants' },
                              { key: 'totalParticipants', label: 'Total Active Participants' },
                              { key: 'totalCompletedSurveys', label: 'Completed Surveys' },
                              { key: 'totalSurveys', label: 'Total Surveys' }
                            ].map(header => (
                              <th
                                key={header.key}
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer group"
                                onClick={() => requestSiteSort(header.key)}
                              >
                                <div className="flex items-center">
                                  {header.label}
                                  <div className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                    {siteSortConfig.key === header.key ? (
                                      siteSortConfig.direction === 'ascending' ? 
                                        <ChevronUp size={14} className="text-primary-600" /> : 
                                        <ChevronDown size={14} className="text-primary-600" />
                                    ) : (
                                      <ChevronDown size={14} className="text-gray-400 dark:text-gray-500" />
                                    )}
                                  </div>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {currentSiteItems.map((site, index) => (
                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="mr-3 flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                    <Building2 size={16} className="text-gray-600 dark:text-gray-300" />
                                  </div>
                                  <span className="font-medium text-gray-900 dark:text-gray-100">{site.siteName}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <ComplianceRateDisplay value={site.complianceRate} />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <CheckCircle size={16} className="text-primary-600 mr-2" />
                                  <span className="text-gray-900 dark:text-gray-100">{site.compliantParticipants}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Users size={16} className="text-gray-600 dark:text-gray-300 mr-2" />
                                  <span className="text-gray-900 dark:text-gray-100">{site.totalParticipants}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-gray-900 dark:text-gray-100">{site.totalCompletedSurveys}</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-gray-900 dark:text-gray-100">{site.totalSurveys}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination for Sites */}
                    {filteredSiteData.length > siteItemsPerPage && (
                      <div className="px-6 py-4 flex justify-between items-center border-t border-gray-200 dark:border-gray-700">
                        <div>
                          <p className="text-sm text-gray-700 dark:text-gray-200">
                            Showing <span className="font-medium">{Math.min(1 + (siteCurrentPage - 1) * siteItemsPerPage, filteredSiteData.length)}</span> to <span className="font-medium">{Math.min(siteCurrentPage * siteItemsPerPage, filteredSiteData.length)}</span> of{' '}
                            <span className="font-medium">{filteredSiteData.length}</span> sites
                          </p>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => setSiteCurrentPage(Math.max(1, siteCurrentPage - 1))}
                            disabled={siteCurrentPage === 1}
                            className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors duration-200 ${
                              siteCurrentPage === 1
                                ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                                : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            }`}
                          >
                            <ArrowLeft className="w-4 h-4" />
                          </button>
                          
                          {Array.from({ length: Math.min(5, Math.ceil(filteredSiteData.length / siteItemsPerPage)) }).map((_, index) => {
                            const pageNumber = index + 1;
                            return (
                              <button
                                key={index}
                                onClick={() => paginateSites(pageNumber)}
                                className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors duration-200 ${
                                  siteCurrentPage === pageNumber
                                    ? "bg-primary-600 text-white"
                                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          })}
                          
                          <button
                            onClick={() => setSiteCurrentPage(Math.min(Math.ceil(filteredSiteData.length / siteItemsPerPage), siteCurrentPage + 1))}
                            disabled={siteCurrentPage === Math.ceil(filteredSiteData.length / siteItemsPerPage)}
                            className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors duration-200 ${
                              siteCurrentPage === Math.ceil(filteredSiteData.length / siteItemsPerPage)
                                ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                                : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            }`}
                          >
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Site Compliance Chart */}
                  <div className="bg-white dark:bg-gray-800 p-6 shadow-sm dark:shadow-gray-900/20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Site Compliance Overview</h2>
                    <div className="h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={siteComplianceChartData}
                          margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
                          barSize={24}
                        >
                          <CartesianGrid
                            vertical={false}
                            stroke="#E5E7EB"
                            strokeDasharray="3 3"
                            opacity={0.5}
                          />
                          <XAxis
                            dataKey="siteName"
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: '#6B7280',
                              fontSize: 12,
                              fontWeight: 500
                            }}
                            dy={10}
                            interval={0}
                            angle={-45}
                            textAnchor="end"
                            height={120}
                          />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{
                              fill: '#6B7280',
                              fontSize: 12
                            }}
                            domain={[0, 100]}
                            ticks={[0, 20, 40, 60, 80, 100]}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(236, 253, 245, 0.4)' }}
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900/20 p-4 border border-gray-100 dark:border-gray-700">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {data.siteName}
                                    </p>
                                    <div className="mt-2 space-y-1">
                                      <p className="text-sm text-gray-600 dark:text-gray-300">
                                        Compliance Rate: <span className="font-medium">{formatComplianceRate(data.complianceRate)}</span>
                                      </p>
                                      <p className="text-sm text-gray-600 dark:text-gray-300">
                                        Compliant Participants: <span className="font-medium">{data.compliantParticipants} of {data.totalParticipants}</span>
                                      </p>
                                      <p className="text-sm text-gray-600 dark:text-gray-300">
                                        Surveys: <span className="font-medium">{data.totalCompletedSurveys} of {data.totalSurveys} completed</span>
                                      </p>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar
                            dataKey="complianceRate"
                            name="Compliance Rate"
                            radius={[4, 4, 0, 0]}
                          >
                          {siteComplianceChartData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getComplianceFill(entry.complianceRate)}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </motion.div>
              </Tab.Panel>

              {/* Form Compliance Tab */}
              <Tab.Panel>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="p-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <StatCard
                      title="Tracked Forms"
                      value={formStats.totalForms}
                      icon={ClipboardList}
                      color="primary"
                    />
                    <StatCard
                      title="Avg. Form Compliance"
                      value={formatComplianceRate(formStats.averageCompliance)}
                      icon={Target}
                      color={Number.isFinite(formStats.averageCompliance) && formStats.averageCompliance >= 80 ? "emerald" : "red"}
                    />
                    <StatCard
                      title="Forms Below 80%"
                      value={formStats.lowComplianceForms}
                      icon={AlertTriangle}
                      color={formStats.lowComplianceForms > 0 ? "red" : "emerald"}
                    />
                    <StatCard
                      title="Form Completion"
                      value={`${formStats.totalCompleted} / ${formStats.totalSurveys}`}
                      icon={CheckCircle}
                      color="blue"
                    />
                  </div>

                  <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900/20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 mb-10">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Form Compliance</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Assessment-level completion metrics across the study. Click a form to see each participant's status.</p>
                      </div>
                      <div className="flex flex-col md:flex-row items-center space-y-3 md:space-y-0 md:space-x-3">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-gray-400 dark:text-gray-500" />
                          </div>
                          <input
                            type="text"
                            className="pl-10 py-2 pr-4 block w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 sm:text-sm"
                            placeholder="Search forms or participant ID..."
                            value={formSearchTerm}
                            onChange={handleFormSearchChange}
                          />
                        </div>
                        <button
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                          onClick={handleFormExport}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Export
                        </button>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                          <tr>
                            {[
                              { key: 'formName', label: 'Form Name' },
                              { key: 'scheduleType', label: 'Schedule Type' },
                              { key: 'complianceRate', label: 'Compliance Rate (%)' },
                              { key: 'completedSurveys', label: 'Completed' },
                              { key: 'inProgressSurveys', label: 'In Progress' },
                              { key: 'pendingSurveys', label: 'Pending' },
                              { key: 'missedSurveys', label: 'Missed' },
                              { key: 'totalSurveys', label: 'Total Expected' },
                              { key: 'assignedParticipants', label: 'Assigned Participants' },
                            ].map(header => (
                              <th
                                key={header.key}
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer group"
                                onClick={() => requestFormSort(header.key)}
                              >
                                <div className="flex items-center">
                                  {header.label}
                                  <div className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                    {formSortConfig.key === header.key ? (
                                      formSortConfig.direction === 'ascending'
                                        ? <ChevronUp size={14} className="text-primary-600" />
                                        : <ChevronDown size={14} className="text-primary-600" />
                                    ) : (
                                      <ChevronDown size={14} className="text-gray-400 dark:text-gray-500" />
                                    )}
                                  </div>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {currentFormItems.length === 0 ? (
                            <tr>
                              <td colSpan={9} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                                No forms match your current search criteria.
                              </td>
                            </tr>
                          ) : (
                            currentFormItems.map((form) => (
                              <tr
                                key={form.formId || form.formName}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 cursor-pointer"
                                onClick={() => openFormParticipants(form)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openFormParticipants(form);
                                  }
                                }}
                                title="View participant status for this form"
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <ClipboardList size={16} className="mr-2 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                                    <span className="font-medium text-primary-600 dark:text-primary-400 hover:underline">{form.formName}</span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                                  {form.scheduleType}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <ComplianceRateDisplay value={form.complianceRate} />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                  {form.completedSurveys}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                  {form.inProgressSurveys}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                  {form.pendingSurveys}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                  {form.missedSurveys}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                  {form.totalSurveys}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                  {form.assignedParticipants}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {filteredFormData.length > formItemsPerPage && (
                      <div className="px-6 py-4 flex justify-between items-center border-t border-gray-200 dark:border-gray-700">
                        <div>
                          <p className="text-sm text-gray-700 dark:text-gray-200">
                            Showing <span className="font-medium">{Math.min(1 + (formCurrentPage - 1) * formItemsPerPage, filteredFormData.length)}</span> to <span className="font-medium">{Math.min(formCurrentPage * formItemsPerPage, filteredFormData.length)}</span> of{' '}
                            <span className="font-medium">{filteredFormData.length}</span> forms
                          </p>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => setFormCurrentPage(Math.max(1, formCurrentPage - 1))}
                            disabled={formCurrentPage === 1}
                            className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors duration-200 ${
                              formCurrentPage === 1
                                ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                                : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            }`}
                          >
                            <ArrowLeft className="w-4 h-4" />
                          </button>

                          {Array.from({ length: Math.min(5, totalFormPages) }).map((_, index) => {
                            const pageNumber = index + 1;
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => setFormCurrentPage(pageNumber)}
                                className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors duration-200 ${
                                  formCurrentPage === pageNumber
                                    ? "bg-primary-600 text-white"
                                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          })}

                          <button
                            onClick={() => setFormCurrentPage(Math.min(totalFormPages, formCurrentPage + 1))}
                            disabled={formCurrentPage === totalFormPages}
                            className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors duration-200 ${
                              formCurrentPage === totalFormPages
                                ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                                : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            }`}
                          >
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              </Tab.Panel>

              {/* Statistics Tab */}
              <Tab.Panel>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="p-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <StatCard
                      title="Total Active Participants"
                      value={complianceStats.totalParticipants}
                      icon={Users}
                      color="purple"
                    />
                    <StatCard
                      title="Compliance Rate"
                      value={formatComplianceRate(complianceStats.complianceRate)}
                      icon={Target}
                      color={Number.isFinite(complianceStats.complianceRate) && complianceStats.complianceRate >= 80 ? "primary" : "red"}
                    />
                    <StatCard
                      title="Surveys Completed"
                      value={`${complianceStats.totalCompletedSurveys} / ${complianceStats.totalSurveys}`}
                      icon={CheckCircle}
                      color="green"
                    />
                    <StatCard
                      title="Avg. Compliance"
                      value={formatComplianceRate(complianceStats.averageCompliance)}
                      icon={TrendingUp}
                      color="blue"
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Compliance Distribution Chart */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Compliance Distribution</h2>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Participant percentages by compliance range
                        </div>
                      </div>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={getComplianceDistribution()}
                            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                            barSize={40}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              vertical={false}
                              stroke="#E5E7EB"
                              opacity={0.5}
                            />
                            <XAxis
                              dataKey="range"
                              axisLine={false}
                              tickLine={false}
                              tick={{
                                fill: '#6B7280',
                                fontSize: 12
                              }}
                              dy={10}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{
                                fill: '#6B7280',
                                fontSize: 12
                              }}
                              dx={-10}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar
                              dataKey="count"
                              name="Participants"
                              radius={[4, 4, 0, 0]}
                            >
                              {getComplianceDistribution().map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={entry.color}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Compliance Pie Chart */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Compliance Status</h2>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Participant compliance overview
                        </div>
                      </div>
                      <div className="h-80 flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
                            <Pie
                              data={getCompliancePieData()}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={3}
                              dataKey="value"
                              label={({ name, percent, cx, cy, midAngle, innerRadius, outerRadius }) => {
                                const RADIAN = Math.PI / 180;
                                const radius = innerRadius + (outerRadius - innerRadius) * 1.4;
                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                
                                return (
                                  <text 
                                    x={x} 
                                    y={y} 
                                    fill="#374151" 
                                    textAnchor={x > cx ? 'start' : 'end'} 
                                    dominantBaseline="central"
                                    fontSize="12"
                                    fontWeight="500"
                                  >
                                    {`${name} (${(percent * 100).toFixed(0)}%)`}
                                  </text>
                                );
                              }}
                              labelLine={false}
                            >
                              {getCompliancePieData().map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value, name) => [`${value} participants`, name]}
                              itemStyle={{ color: '#1F2937' }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      
                      <div className="flex justify-center mt-4">
                        <div className="flex items-center space-x-8">
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-primary-500 mr-2"></div>
                            <span className="text-sm text-gray-700 dark:text-gray-200">Compliant (&gt;=80%)</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                            <span className="text-sm text-gray-700 dark:text-gray-200">Non-Compliant (&lt;80%)</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Site Compliance Comparison */}
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 lg:col-span-2">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Site Compliance Comparison</h2>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {siteComplianceData.length} sites ranked by compliance rate
                        </div>
                      </div>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={siteComplianceChartData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 120 }}
                            barSize={20}
                          >
                            <CartesianGrid
                              vertical={false}
                              stroke="#E5E7EB"
                              strokeDasharray="3 3"
                              opacity={0.5}
                            />
                            <XAxis
                              dataKey="siteName"
                              axisLine={false}
                              tickLine={false}
                              tick={{
                                fill: '#6B7280',
                                fontSize: 12,
                                fontWeight: 500
                              }}
                              dy={10}
                              interval={0}
                              angle={-45}
                              textAnchor="end"
                              height={100}
                            />
                            <YAxis
                              axisLine={false}
                              tickLine={false}
                              tick={{
                                fill: '#6B7280',
                                fontSize: 12
                              }}
                              domain={[0, 100]}
                              ticks={[0, 20, 40, 60, 80, 100]}
                            />
                            <Tooltip
                              cursor={{ fill: 'rgba(236, 253, 245, 0.4)' }}
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900/20 p-4 border border-gray-100 dark:border-gray-700">
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {data.siteName}
                                      </p>
                                      <div className="mt-2 space-y-1">
                                        <p className="text-sm text-gray-600 dark:text-gray-300">
                                          Compliance Rate: <span className="font-medium">{formatComplianceRate(data.complianceRate)}</span>
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">
                                          Compliant Participants: <span className="font-medium">{data.compliantParticipants} of {data.totalParticipants}</span>
                                        </p>
                                        <p className="text-sm text-gray-600 dark:text-gray-300">
                                          Surveys: <span className="font-medium">{data.totalCompletedSurveys} of {data.totalSurveys} completed</span>
                                        </p>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Bar
                              dataKey="complianceRate"
                              name="Compliance Rate"
                              radius={[4, 4, 0, 0]}
                            >
                              {siteComplianceChartData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={getComplianceFill(entry.complianceRate)}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      
                      <div className="flex justify-center mt-4">
                        <div className="flex flex-wrap justify-center gap-4">
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-primary-500 mr-2"></div>
                            <span className="text-sm text-gray-700 dark:text-gray-200">80-100%</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-emerald-400 mr-2"></div>
                            <span className="text-sm text-gray-700 dark:text-gray-200">60-79%</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2"></div>
                            <span className="text-sm text-gray-700 dark:text-gray-200">40-59%</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-orange-400 mr-2"></div>
                            <span className="text-sm text-gray-700 dark:text-gray-200">20-39%</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                            <span className="text-sm text-gray-700 dark:text-gray-200">0-19%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>

      {/* FECOA-1192: Form drill-down — participants Pending/Missed a form */}
      <AnimatePresence>
        {selectedForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={closeFormParticipants}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 w-full max-w-5xl max-h-[88vh] flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-overlay dark:shadow-gray-900/40 overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-label={`Participant status for ${selectedForm.formName}`}
            >
              {/* Header */}
              <div className="flex items-start justify-between px-6 pt-6 pb-5 border-b border-gray-200 dark:border-gray-700">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{selectedForm.formName}</h2>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Participant status for this form
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeFormParticipants}
                  className="ml-4 -mr-1.5 -mt-1 flex-shrink-0 rounded-lg p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Summary stats — hairline-divided metric bar */}
              <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                <div className="flex overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 divide-x divide-gray-200 dark:divide-gray-700">
                  {[
                    { label: 'Participants', value: formTotalParticipants, dot: 'bg-gray-300 dark:bg-gray-600' },
                    { label: 'Completed', value: formParticipantCounts.completed, dot: 'bg-emerald-500' },
                    { label: 'In Progress', value: formParticipantCounts.in_progress, dot: 'bg-blue-500' },
                    { label: 'Pending', value: formParticipantCounts.pending, dot: 'bg-yellow-500' },
                    { label: 'Missed', value: formParticipantCounts.missed, dot: 'bg-red-500' },
                  ].map((stat) => (
                    <div key={stat.label} className="flex-1 min-w-[120px] px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className={classNames('h-1.5 w-1.5 rounded-full', stat.dot)} />
                        <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">{stat.label}</span>
                      </div>
                      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                        {formParticipantsLoading ? '—' : stat.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Search + filter chips */}
              <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  {[
                    { key: 'all', label: `All (${formParticipantCounts.total})` },
                    { key: 'completed', label: `Completed (${formParticipantCounts.completed})` },
                    { key: 'in_progress', label: `In Progress (${formParticipantCounts.in_progress})` },
                    { key: 'pending', label: `Pending (${formParticipantCounts.pending})` },
                    { key: 'missed', label: `Missed (${formParticipantCounts.missed})` },
                  ].map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => setFormStatusFilter(chip.key)}
                      className={classNames(
                        'px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-150',
                        formStatusFilter === chip.key
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                      )}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
                <div className="relative w-full lg:w-96">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={16} className="text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    className="pl-10 py-2 pr-4 block w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 sm:text-sm"
                    placeholder="Search participant or site…"
                    value={formParticipantSearch}
                    onChange={(e) => setFormParticipantSearch(e.target.value)}
                  />
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto">
                {formParticipantsLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader className="w-6 h-6 text-primary-500 animate-spin" />
                    <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading participants…</span>
                  </div>
                ) : formParticipantsError ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <AlertTriangle className="w-8 h-8 text-red-500 mb-3" />
                    <p className="text-sm text-gray-600 dark:text-gray-300">{formParticipantsError}</p>
                    <button
                      type="button"
                      onClick={() => openFormParticipants(selectedForm)}
                      className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
                    >
                      Retry
                    </button>
                  </div>
                ) : visibleFormParticipants.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-500 mb-3" />
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {formParticipantCounts.total === 0
                        ? 'No participants are assigned to this form yet.'
                        : 'No participants match your filter or search.'}
                    </p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th scope="col" className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-900 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Participant ID</th>
                        <th scope="col" className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-900 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Site</th>
                        <th scope="col" className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-900 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th scope="col" className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-900 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Due Date / Window End</th>
                        <th scope="col" className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-900 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Activity</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {visibleFormParticipants.map((p, idx) => {
                        const hasLink = p.site_id && p.participantUUID && studyId;
                        const dueDate = p.endTime ? new Date(p.endTime) : null;
                        const lastActivity = p.lastActivity ? new Date(p.lastActivity) : null;
                        const badge = FORM_STATUS_BADGE[p.status] || FORM_STATUS_BADGE.pending;
                        const BadgeIcon = badge.icon;
                        return (
                          <tr key={`${p.windowId || p.participantUUID || idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {hasLink ? (
                                <Link
                                  to={`/studies/${studyId}/sites/${p.site_id}/participant/${p.participantUUID}`}
                                  className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:underline font-medium"
                                  onClick={closeFormParticipants}
                                >
                                  {p.participantId || '—'}
                                  <ExternalLink className="w-3.5 h-3.5 ml-1 opacity-70" />
                                </Link>
                              ) : (
                                <span className="text-gray-900 dark:text-gray-100 font-medium">{p.participantId || '—'}</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-200">
                              <span className="inline-flex items-center">
                                <MapPin className="w-3.5 h-3.5 mr-1 text-gray-400 dark:text-gray-500" />
                                {p.site || '—'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={classNames('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', badge.className)}>
                                <BadgeIcon className="w-3.5 h-3.5 mr-1" />
                                {badge.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {dueDate
                                ? dueDate.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {lastActivity
                                ? lastActivity.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50/60 dark:bg-gray-900/40">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formParticipantsLoading
                    ? 'Loading…'
                    : `Showing ${visibleFormParticipants.length} of ${formParticipantCounts.total}`}
                </span>
                <button
                  type="button"
                  onClick={closeFormParticipants}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompliancePage;